/**
 * The document API: a template filled from any JSON, with no Lark record behind it.
 *
 * `POST /api/v1/templates/:templateId/documents` takes `{ data, … }` and dispatches the
 * data to the generation worker as a `direct` data source — the path approval templates
 * already use — so everything after the dispatch (progress, R2, charging, the outgoing
 * webhook) is the ordinary generation path. This file is the part that has to agree with
 * the template: how a JSON key becomes a placeholder name, and which values become pictures.
 *
 * **Naming is the template's own rule, not a second one.** Every key goes through
 * `sanitizeFieldName()` — the function that names a Base field's or an approval widget's
 * variable — so `"Invoice No."` fills `{Invoice_No_}` exactly as a Base field of that
 * name would. Matching is case-sensitive, as placeholders are. Two keys of one object that
 * sanitize to the same name are refused (`DATA_NAME_COLLISION`) rather than numbered the
 * way approval widgets are: a caller cannot see a suffix it was never told about, and a
 * document silently filled from the wrong key is worse than a 400.
 *
 * Pure and dependency-free: it runs in the API Worker and in tests.
 */

import { sanitizeFieldName } from './fields'
import type { PlaceholderNode } from './api'
import type { AutomatedSignatureOutcome } from './signature'
import { MAX_COMBINED_RECORDS, isCombinedMode, type RecordMode } from './combined'
import type { OutputFormat, TemplateFormat } from './templateFormat'
import type { TemplateSourceType } from './templateSource'

// =============================================================================
// Limits
// =============================================================================

/**
 * What one request may carry. Each refusal has its own code (DocumentApiErrorCode), so a
 * caller's script can say which limit it hit.
 */
export const DOCUMENT_API_LIMITS = {
  /** Request body, in bytes. Far above any real document's data; well under a Worker's memory. */
  maxBodyBytes: 1_000_000,
  /** Objects and lists nested inside one item (the item itself is depth 1). */
  maxDepth: 8,
  /** Entries in any one list: loop rows, pictures, a list of words. */
  maxArrayLength: 1_000,
  /** Keys in any one object. */
  maxKeysPerObject: 500,
  /** Characters in any one string. */
  maxStringLength: 100_000,
  /** Items when `data` is a list and each makes a document. */
  maxBatchItems: 50,
  /** Items when `data` is a list that makes one combined document (MAX_COMBINED_RECORDS). */
  maxCombinedItems: MAX_COMBINED_RECORDS,
  /** Pictures fetched for one document (for one item of a combined document). */
  maxImagesPerDocument: 50,
  /**
   * Pictures fetched for one combined document, all its items together: as many as a
   * 500-record Generate with a picture each. Per item alone it would be 25,000 fetches.
   */
  maxImagesPerCombinedDocument: 500,
  /** `?wait=` on the POST, in seconds. Under the 30 seconds most HTTP clients and Lark's HTTP node wait. */
  maxWaitSeconds: 25,
  /** A filename pattern override, in characters. */
  maxFilenameLength: 200,
  /** A caller-chosen `recordId` (printed by `{Record_ID}`), in characters. */
  maxRecordIdLength: 100,
  /** A picture's URL, in characters. */
  maxUrlLength: 2_048,
} as const

/** How many requests a minute one template accepts through the document API (the API_RATE_LIMITER binding). */
export const DOCUMENT_API_RATE_LIMIT_PER_MINUTE = 60

/**
 * How many requests a minute one template accepts through the HTTP trigger (TRIGGER_RATE_LIMITER).
 * Its own budget, higher than the document API's: a Lark automation fires once per record and
 * never retries a refused call, so a bulk import over the limit would lose its documents.
 */
export const HTTP_TRIGGER_RATE_LIMIT_PER_MINUTE = 300

/** How many documents a minute one company may request through the document API and the HTTP trigger together (API_COMPANY_RATE_LIMITER). */
export const DOCUMENT_API_COMPANY_DOCUMENTS_PER_MINUTE = 600

/** Names the generation worker sets on every record; a key of the same name in `data` is replaced. */
export const DOCUMENT_API_RESERVED_NAMES = ['Record_ID', 'Template_Name', 'Print_Time'] as const

// =============================================================================
// Request
// =============================================================================

/** `pdf`, `png` (the first page as an image) or the template's own file format. */
export type DocumentApiFormat = 'pdf' | 'png' | 'native'
export const DOCUMENT_API_FORMATS: readonly DocumentApiFormat[] = ['pdf', 'png', 'native']

/**
 * How the items become documents. It is the template's record mode (combined.ts), not a
 * choice the request makes: a list template has a `{#Records}` table, a labels template a
 * label-sized body. `separate` — a document per item — is a per-record template; `list`,
 * `continuous` and `labels` are one document from all the items, which become its
 * `Records`. Sent, `mode` must name what the template makes (`MODE_MISMATCH` otherwise), so
 * a script written for one kind of template fails loudly on another; left out, the
 * template decides.
 */
export type DocumentApiBatchMode = 'separate' | 'list' | 'continuous' | 'labels'
export const DOCUMENT_API_BATCH_MODES: readonly DocumentApiBatchMode[] = ['separate', 'list', 'continuous', 'labels']

export interface CreateDocumentsRequest {
  /** The record: its keys are the placeholders. A list makes one document per item. */
  data: Record<string, unknown> | Record<string, unknown>[]
  /** Filename pattern for this request, in place of the template's (`{Field}` placeholders allowed). */
  filename?: string
  /** Output format; the template's setting when absent. */
  format?: DocumentApiFormat
  /** What `{Record_ID}` prints for a single document. Defaults to the generation id. */
  recordId?: string
  /** What the template makes; see DocumentApiBatchMode. */
  mode?: DocumentApiBatchMode
  /** Send the document for signature once generated. See the API docs; validated by the API. */
  sign?: unknown
}

/**
 * Every code the request checks of this file and the handler's own option checks refuse
 * with. A list, not only a type, so the published OpenAPI description can be tested
 * against it (apps/api/worker/src/openapi): a code added here and not documented fails.
 */
export const DOCUMENT_API_ERROR_CODES = [
  'INVALID_JSON',
  'BODY_TOO_LARGE',
  'DATA_REQUIRED',
  'DATA_NOT_OBJECT',
  'DATA_TOO_DEEP',
  'DATA_ARRAY_TOO_LONG',
  'DATA_TOO_MANY_KEYS',
  'DATA_STRING_TOO_LONG',
  'DATA_MIXED_ARRAY',
  'DATA_INVALID_NAME',
  'DATA_NAME_COLLISION',
  'BATCH_EMPTY',
  'BATCH_TOO_LARGE',
  'MODE_MISMATCH',
  'INVALID_FORMAT',
  'INVALID_FILENAME',
  'INVALID_RECORD_ID',
  'INVALID_MODE',
  'INVALID_WAIT',
  'INVALID_ASYNC',
  'IMAGE_URL_NOT_ALLOWED',
  'TOO_MANY_IMAGES',
] as const

export type DocumentApiErrorCode = (typeof DOCUMENT_API_ERROR_CODES)[number]

export interface DocumentApiError {
  code: DocumentApiErrorCode
  /** English, for logs and the caller's developer. */
  error: string
  /** Where in the request: `data[2].Items[0].Photo`. */
  path?: string
}

export interface ParsedDocumentRequest {
  /** One per document to generate. */
  items: Record<string, unknown>[]
  /** `data` was a list (the response then lists documents). */
  batch: boolean
  filename: string | null
  format: DocumentApiFormat | null
  recordId: string | null
  /** As sent; null when the template is to decide. */
  mode: DocumentApiBatchMode | null
  /** Present when asked for; the API validates it against what signing supports. */
  sign: unknown
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const fail = (code: DocumentApiErrorCode, error: string, path?: string): { ok: false; error: DocumentApiError } => ({
  ok: false,
  error: path ? { code, error, path } : { code, error },
})

/**
 * Check a request body's shape and options. The data itself is checked item by item by
 * documentRecordFromJson(), which also needs the template.
 */
export function parseDocumentRequest(body: unknown): { ok: true; request: ParsedDocumentRequest } | { ok: false; error: DocumentApiError } {
  const L = DOCUMENT_API_LIMITS
  if (!isPlainObject(body)) return fail('INVALID_JSON', 'The body must be a JSON object')
  const { data } = body
  if (data === undefined || data === null) return fail('DATA_REQUIRED', '`data` is required')

  let items: Record<string, unknown>[]
  const batch = Array.isArray(data)
  if (batch) {
    if (data.length === 0) return fail('BATCH_EMPTY', '`data` is an empty list')
    // The larger cap here; the API applies the smaller one once it knows the template makes a document per item.
    if (data.length > L.maxCombinedItems) return fail('BATCH_TOO_LARGE', `\`data\` may list at most ${L.maxCombinedItems} items`)
    const bad = data.findIndex(item => !isPlainObject(item))
    if (bad >= 0) return fail('DATA_NOT_OBJECT', 'Every item of `data` must be a JSON object', `data[${bad}]`)
    items = data as Record<string, unknown>[]
  } else {
    if (!isPlainObject(data)) return fail('DATA_NOT_OBJECT', '`data` must be a JSON object or a list of them')
    items = [data]
  }

  let filename: string | null = null
  if (body.filename !== undefined && body.filename !== null) {
    if (typeof body.filename !== 'string' || !body.filename.trim() || body.filename.length > L.maxFilenameLength) {
      return fail('INVALID_FILENAME', `\`filename\` must be a non-empty string of at most ${L.maxFilenameLength} characters`)
    }
    filename = body.filename.trim()
  }

  let format: DocumentApiFormat | null = null
  if (body.format !== undefined && body.format !== null) {
    if (!(DOCUMENT_API_FORMATS as readonly unknown[]).includes(body.format)) {
      return fail('INVALID_FORMAT', '`format` must be "pdf", "png" or "native"')
    }
    format = body.format as DocumentApiFormat
  }

  let recordId: string | null = null
  if (body.recordId !== undefined && body.recordId !== null) {
    if (batch) return fail('INVALID_RECORD_ID', '`recordId` names one document; it cannot be used with a list')
    if (typeof body.recordId !== 'string' || !body.recordId.trim() || body.recordId.length > L.maxRecordIdLength) {
      return fail('INVALID_RECORD_ID', `\`recordId\` must be a non-empty string of at most ${L.maxRecordIdLength} characters`)
    }
    recordId = body.recordId.trim()
  }

  let mode: DocumentApiBatchMode | null = null
  if (body.mode !== undefined && body.mode !== null) {
    if (!(DOCUMENT_API_BATCH_MODES as readonly unknown[]).includes(body.mode)) {
      return fail('INVALID_MODE', '`mode` must be "separate", "list", "continuous" or "labels"')
    }
    mode = body.mode as DocumentApiBatchMode
  }

  return { ok: true, request: { items, batch, filename, format, recordId, mode, sign: body.sign ?? null } }
}

/** `?wait=` as seconds, 0 when absent. Anything but a whole number in range is refused. */
export function parseWaitSeconds(raw: string | undefined | null): { ok: true; seconds: number } | { ok: false; error: DocumentApiError } {
  if (raw === undefined || raw === null || raw === '') return { ok: true, seconds: 0 }
  if (!/^\d{1,3}$/.test(raw) || Number(raw) > DOCUMENT_API_LIMITS.maxWaitSeconds) {
    return fail('INVALID_WAIT', `\`wait\` must be a whole number of seconds from 0 to ${DOCUMENT_API_LIMITS.maxWaitSeconds}`)
  }
  return { ok: true, seconds: Number(raw) }
}

/**
 * How long a poller following `?async=true` is told to wait between reads (`Retry-After`).
 * Twelve reads a minute stay well inside a document's 60 status reads a minute.
 */
export const DOCUMENT_API_POLL_SECONDS = 5

/**
 * `?async=true` — the polling convention of Power Automate, Logic Apps and other workflow
 * engines: while the document is being made the answer is `202 Accepted` with a `Location`
 * to poll and a `Retry-After`, and the poll answers `202` until the document is done. The
 * engine then hands the finished document to the next step by itself. Opt-in, because a
 * `Location` on the POST would change what an HTTP action that already calls this API
 * does: such an action follows it on its own.
 */
export function parseAsyncFlag(raw: string | undefined | null): { ok: true; async: boolean } | { ok: false; error: DocumentApiError } {
  if (raw === undefined || raw === null || raw === '' || raw === 'false') return { ok: true, async: false }
  if (raw === 'true') return { ok: true, async: true }
  return fail('INVALID_ASYNC', '`async` must be "true" or "false"')
}

// =============================================================================
// Pictures
// =============================================================================

/**
 * The paths the template uses as pictures (by Alt Text), loop rows included, without
 * indexes: `Logo`, `Photos` for `{Photos[1]}`, `Items.Photo` for a picture in a loop row,
 * and `Photos` for a loop of pictures — `{#Photos}` around a picture whose Alt Text is
 * `{.}` (or `{%src}`), which the tree stores as the row itself, `Photos..`.
 * Read from the placeholder tree stored when the file was uploaded (`templates.placeholders`).
 *
 * Only these paths turn a URL into a picture. A URL anywhere else stays text, so a
 * `{Website}` placeholder prints the address it was given. A picture loop must be here
 * by its own name: its rows are the pictures, so a list of URLs sent for it has to become
 * a list of pictures — left as text it is joined into one line the loop shows nothing of.
 */
export function imagePlaceholderPaths(placeholdersJson: string | null | undefined): Set<string> {
  const paths = new Set<string>()
  if (!placeholdersJson) return paths
  let nodes: unknown
  try {
    nodes = JSON.parse(placeholdersJson)
  } catch {
    return paths
  }
  const walk = (list: unknown) => {
    if (!Array.isArray(list)) return
    for (const node of list as PlaceholderNode[]) {
      if (!node || typeof node.variable !== 'string') continue
      if (node.fieldType === 'image') paths.add(node.variable.replace(/\[\d+\]/g, '').replace(/\.\.$/, ''))
      walk(node.nested)
    }
  }
  walk(nodes)
  return paths
}

/**
 * The picture paths an item of a combined document is checked against. A list template's
 * row fields are stored under the loop (`Records.Photo`) while each item is mapped on its
 * own (`Photo`), so the loop's prefix is taken off; a page-per-record or labels template
 * stores them unprefixed already.
 */
export function combinedItemImagePaths(paths: Set<string>): Set<string> {
  const out = new Set<string>()
  for (const path of paths) out.add(path.startsWith('Records.') ? path.slice('Records.'.length) : path)
  return out
}

/**
 * Hostnames that name the caller's own network rather than the internet, and the public
 * wildcard-DNS services that resolve a name to any address written in it
 * (`10.0.0.1.nip.io`) — the easy way round a check on literal addresses.
 */
const PRIVATE_HOST_SUFFIXES = [
  'localhost', 'local', 'internal', 'intranet', 'lan', 'home.arpa', 'svc', 'cluster.local', 'corp',
  'nip.io', 'sslip.io', 'xip.io', 'localtest.me', 'lvh.me',
]

function isPrivateIpv4(host: string): boolean {
  const m = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/)
  if (!m) return false
  const [a, b, c] = [Number(m[1]), Number(m[2]), Number(m[3])]
  return (
    a === 0 || a === 10 || a === 127 || a >= 224 ||
    (a === 100 && b >= 64 && b <= 127) || // carrier-grade NAT
    (a === 169 && b === 254) || // link-local, cloud metadata
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168) ||
    (a === 192 && b === 0 && (c === 0 || c === 2)) || // IETF protocol assignments, TEST-NET-1
    (a === 198 && (b === 18 || b === 19)) ||
    (a === 198 && b === 51 && c === 100) || // TEST-NET-2
    (a === 203 && b === 0 && c === 113) // TEST-NET-3
  )
}

/**
 * Whether the generation worker may fetch a picture from `url`.
 *
 * The worker (or FlyIMG in front of it) fetches it from inside the cluster, so a URL that
 * names a private address would turn the API into a way of reaching services that are not
 * on the internet. Only http(s) to a public-looking hostname or a public IPv4 address
 * passes; IPv6 literals, single-label hosts, internal suffixes and credentials in the URL
 * do not. A hostname that *resolves* to a private address (a cluster Service written as
 * `<service>.<namespace>`) or that redirects to one is not caught here — the Worker cannot
 * resolve names and never sees the redirect. The generation worker checks both again
 * (`utils/publicUrl.ts`, which keeps a copy of this rule); what FlyIMG fetches on its own
 * is the egress policy's job.
 */
export function isAllowedImageUrl(url: string): boolean {
  if (url.length > DOCUMENT_API_LIMITS.maxUrlLength) return false
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false
  if (parsed.username || parsed.password) return false
  const host = parsed.hostname.toLowerCase().replace(/\.$/, '')
  if (!host || host.startsWith('[')) return false
  if (/^\d+(\.\d+){0,3}$/.test(host) || /^0x/i.test(host)) {
    // Only a dotted-quad IPv4 literal is read as an address; decimal and hex forms are refused.
    return /^\d{1,3}(\.\d{1,3}){3}$/.test(host) && !isPrivateIpv4(host)
  }
  if (!host.includes('.')) return false
  return !PRIVATE_HOST_SUFFIXES.some(suffix => host === suffix || host.endsWith(`.${suffix}`))
}

const looksLikeUrl = (value: string) => /^https?:\/\//i.test(value.trim())

/**
 * Whether `namePath` (where a value sits in the request, loops included: `Groups.Items.Photo`)
 * is a picture path of the template. The placeholder tree names a loop's fields by that loop
 * alone — a loop nested in `{#Groups}` is `Items`, its picture `Items.Photo` — so a path also
 * counts when it ends with one: a picture in a nested loop was sent as text and never filled.
 */
function isImagePath(paths: ReadonlySet<string>, namePath: string): boolean {
  if (paths.has(namePath)) return true
  // Only a loop's own path (`Items.Photo`): a bare top-level `Photo` must not make every
  // loop row's `Photo` a picture
  for (let at = namePath.indexOf('.'); at >= 0; at = namePath.indexOf('.', at + 1)) {
    const tail = namePath.slice(at + 1)
    if (tail.includes('.') && paths.has(tail)) return true
  }
  return false
}

// =============================================================================
// Data → record
// =============================================================================

export interface DocumentRecord {
  /** What the template renders: sanitized names, loops as lists of objects. */
  record: Record<string, unknown>
  /** Picture token → URL, for the direct data source's image source. */
  imageUrls: Record<string, string>
  /** Things the caller should know that did not stop the document. */
  warnings: string[]
}

export interface DocumentRecordOptions {
  /** imagePlaceholderPaths() of the template. */
  imagePaths: ReadonlySet<string>
  /** Makes picture tokens unique across the items of one job. */
  tokenPrefix: string
  /** Where this item sits in the request, for error paths: `data` or `data[3]`. */
  path: string
}

/**
 * Turn one item of `data` into the record a template renders.
 *
 * - Keys: `sanitizeFieldName()`, case kept — the naming rule every other data source uses.
 * - Objects nest: `{customer: {name}}` fills `{customer.name}`.
 * - A list of objects is a loop: `{#items}…{/items}`.
 * - A list of plain values prints joined with ", " (as a multi-select field does), and an
 *   empty list prints nothing, so `{#if Tags}` is false for `[]`.
 * - `null` prints nothing. Numbers stay numbers, so `number`, `currency` and `sum` work;
 *   `true`/`false` stay booleans, so `{#if Paid}` reads them as they say.
 * - At a path the template uses as a picture, an http(s) URL — or a list of them — becomes
 *   a picture, the way an approval form's pictures are passed. Anywhere else it is text.
 */
export function documentRecordFromJson(
  item: Record<string, unknown>,
  options: DocumentRecordOptions,
): { ok: true; value: DocumentRecord } | { ok: false; error: DocumentApiError } {
  const L = DOCUMENT_API_LIMITS
  const imageUrls: Record<string, string> = {}
  const warnings: string[] = []
  let imageCount = 0
  let failure: DocumentApiError | null = null

  const stop = (code: DocumentApiErrorCode, error: string, path: string): undefined => {
    failure ??= { code, error, path }
    return undefined
  }

  const picture = (url: string, path: string): Record<string, unknown> | undefined => {
    if (!isAllowedImageUrl(url)) return stop('IMAGE_URL_NOT_ALLOWED', 'Pictures must be http(s) URLs on a public host', path)
    if (++imageCount > L.maxImagesPerDocument) {
      return stop('TOO_MANY_IMAGES', `A document may have at most ${L.maxImagesPerDocument} pictures`, path)
    }
    const token = `${options.tokenPrefix}${imageCount}`
    imageUrls[token] = url
    let name = `Image ${imageCount}`
    try {
      name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '') || name
    } catch {
      // The name is decoration; the URL already passed.
    }
    // Attachment-shaped, as the image placeholder path expects. The bytes decide the
    // real format; the type only has to say "an image".
    return { file_token: token, type: 'image/jpeg', name, _fieldId: 'api', _recordId: 'api' }
  }

  const convert = (value: unknown, path: string, namePath: string, depth: number): unknown => {
    if (failure) return undefined
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') {
      if (value.length > L.maxStringLength) return stop('DATA_STRING_TOO_LONG', `Strings may be at most ${L.maxStringLength} characters`, path)
      if (isImagePath(options.imagePaths, namePath) && looksLikeUrl(value)) {
        const att = picture(value.trim(), path)
        return att ? [att] : undefined
      }
      return value
    }
    if (typeof value === 'number' || typeof value === 'boolean') return value
    if (depth >= L.maxDepth) return stop('DATA_TOO_DEEP', `Data may nest at most ${L.maxDepth} levels`, path)

    if (Array.isArray(value)) {
      if (value.length > L.maxArrayLength) return stop('DATA_ARRAY_TOO_LONG', `Lists may have at most ${L.maxArrayLength} entries`, path)
      const present = value.filter(v => v !== null && v !== undefined)
      if (present.length === 0) return ''
      if (present.every(isPlainObject)) {
        return value.map((row, i) => (isPlainObject(row) ? convertObject(row, `${path}[${i}]`, namePath, depth + 1) : {}))
      }
      if (present.every(v => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
        if (isImagePath(options.imagePaths, namePath) && present.every(v => typeof v === 'string' && looksLikeUrl(v))) {
          const pictures = present.map((v, i) => picture((v as string).trim(), `${path}[${i}]`))
          return failure ? undefined : pictures
        }
        const long = present.findIndex(v => typeof v === 'string' && v.length > L.maxStringLength)
        if (long >= 0) return stop('DATA_STRING_TOO_LONG', `Strings may be at most ${L.maxStringLength} characters`, `${path}[${long}]`)
        return present.map(String).join(', ')
      }
      return stop('DATA_MIXED_ARRAY', 'A list must hold only objects (a loop) or only plain values', path)
    }
    if (isPlainObject(value)) return convertObject(value, path, namePath, depth + 1)
    return stop('DATA_NOT_OBJECT', 'Unsupported value', path)
  }

  const convertObject = (obj: Record<string, unknown>, path: string, namePath: string, depth: number): Record<string, unknown> | undefined => {
    if (failure) return undefined
    const keys = Object.keys(obj)
    if (keys.length > L.maxKeysPerObject) return stop('DATA_TOO_MANY_KEYS', `Objects may have at most ${L.maxKeysPerObject} keys`, path)
    const out: Record<string, unknown> = {}
    const from = new Map<string, string>()
    for (const key of keys) {
      const name = sanitizeFieldName(key)
      const keyPath = `${path}.${key}`
      if (!name || /^_+$/.test(name)) return stop('DATA_INVALID_NAME', `"${key}" has no letter or digit to name a placeholder by`, keyPath)
      const clash = from.get(name)
      if (clash !== undefined) {
        return stop('DATA_NAME_COLLISION', `"${clash}" and "${key}" both fill {${name}}`, keyPath)
      }
      from.set(name, key)
      const converted = convert(obj[key], keyPath, namePath ? `${namePath}.${name}` : name, depth)
      if (failure) return undefined
      out[name] = converted
    }
    return out
  }

  const record = convertObject(item, options.path, '', 1)
  if (failure || !record) return { ok: false, error: failure ?? { code: 'DATA_NOT_OBJECT', error: 'Unsupported value', path: options.path } }
  for (const reserved of DOCUMENT_API_RESERVED_NAMES) {
    if (reserved in record) warnings.push(`"${reserved}" is set by AutomaDoc; the value given for it was replaced`)
  }
  return { ok: true, value: { record, imageUrls, warnings } }
}

// =============================================================================
// Responses
// =============================================================================

export type DocumentApiStatus = 'processing' | 'completed' | 'failed'

/** One document, as `POST …/documents` and `GET /api/v1/documents/:id` report it. */
export interface DocumentApiDocument {
  generationId: string
  templateId: string
  status: DocumentApiStatus
  /** Poll this with the same key until `status` is not `processing`. */
  statusUrl: string
  createdAt: number
  /** Once completed. */
  filename?: string
  /** Once completed: a signed link, no key needed, valid until `downloadUrlExpiresAt`. */
  downloadUrl?: string
  downloadUrlExpiresAt?: number
  /** Once failed: the generation worker's reason. */
  error?: string
  /**
   * Things that did not stop the document: on the POST, what was wrong with the data (a
   * reserved name replaced); once completed, on the POST and the status route alike, what
   * the generation warned about — a picture that could not be fetched, named by field and host.
   */
  warnings?: string[]
  /** When the request asked for a signature (`sign`): what became of it. */
  signing?: DocumentApiSigning
  /** A combined document: how many items it was made from. */
  recordCount?: number
}

/** The record mode a request's `mode` names. */
export function recordModeOfBatchMode(mode: DocumentApiBatchMode): RecordMode {
  return mode === 'separate' ? 'per_record' : mode
}

/**
 * A document's signature request, as its status reports it. Outside signers' links and
 * generated passwords are in `links` the first time the status is read after the
 * request started, and never again: later reads list whose links they were, with `url`
 * and `password` null and `linksRedacted` set. Colleagues are asked by the bot.
 */
export interface DocumentApiSigning extends AutomatedSignatureOutcome {
  links?: Array<{ role: string; displayName: string; url: string | null; password: string | null }>
  linksRedacted?: boolean
}

/** The POST's body: one document, or `documents` when `data` was a list. */
export type CreateDocumentsResponse = DocumentApiDocument | { documents: DocumentApiDocument[] }

/** The prefix every tenant-wide API key starts with, which is how a request's key is told apart from a template's. */
export const TENANT_API_KEY_PREFIX = 'amk_'

/** How many live tenant-wide keys a company may hold. */
export const MAX_TENANT_API_KEYS = 20

/** A tenant-wide API key as the portal lists it. The secret is shown once, at creation, and never again. */
export interface TenantApiKeySummary {
  id: string
  name: string
  /** `amk_<id>_…<last four>`, to recognise a key without revealing it. */
  hint: string
  createdAt: number
  createdByName: string | null
  lastUsedAt: number | null
}

export interface CreateTenantApiKeyResponse {
  key: TenantApiKeySummary
  /** The whole key. Shown once. */
  secret: string
}

// =============================================================================
// Examples for the UI
// =============================================================================

/** Names the generation worker or the extension supply themselves; never part of `data`. */
const SYSTEM_NAMES = new Set<string>([...DOCUMENT_API_RESERVED_NAMES, 'Table_Name'])

/** Names a combined document has of its own (combinedDocumentData in the worker), never an item's. */
const COMBINED_DOCUMENT_NAMES = new Set(['Records', 'RecordCount', 'Date'])

/**
 * What a template asks of `data`, read from its stored placeholder tree. One walk behind
 * both the copyable example (sampleDocumentData) and the schema a flow designer renders its
 * input fields from (documentDataSchema), so the two cannot disagree on a name.
 */
type DataShape =
  | { kind: 'text' }
  | { kind: 'picture' }
  /** Only ever tested: `{#if Paid}`. */
  | { kind: 'flag' }
  /** Only ever compared: `{#if Status == "Paid"}`. */
  | { kind: 'compare' }
  | { kind: 'object'; fields: Record<string, DataShape> }
  /** A loop: rows of fields, or rows printed whole (`{.}`) — text or pictures. */
  | { kind: 'list'; row: DataShape }

const isCondition = (shape: DataShape) => shape.kind === 'flag' || shape.kind === 'compare'

function parsePlaceholderTree(placeholdersJson: string | null | undefined): unknown {
  try {
    return placeholdersJson ? JSON.parse(placeholdersJson) : []
  } catch {
    return []
  }
}

function shapeOfNodes(list: unknown, prefix: string): Record<string, DataShape> {
  const out: Record<string, DataShape> = {}
  if (!Array.isArray(list)) return out
  for (const node of list as PlaceholderNode[]) {
    if (!node || typeof node.variable !== 'string' || node.variable.endsWith('..')) continue
    // Not data. A signature placeholder names a role, which `sign` fills; an attachment's
    // files cannot be sent as JSON (the API docs say so), so asking for them would mislead.
    if (node.fieldType === 'signature' || node.fieldType === 'attachment') continue
    const relative = prefix && node.variable.startsWith(`${prefix}.`) ? node.variable.slice(prefix.length + 1) : node.variable
    const key = relative.split('.')[0].replace(/\[\d+\]$/, '')
    if (!key || (!prefix && SYSTEM_NAMES.has(key))) continue
    const shape = shapeOfNode(node, prefix ? `${prefix}.${key}` : key)
    // The first use names the field, except that a value beats a condition on the same
    // name: `{Status}` and `{#if Status == "Paid"}` are one text field, not a yes/no.
    const existing = out[key]
    if (existing && !(isCondition(existing) && !isCondition(shape))) continue
    out[key] = shape
  }
  return out
}

function shapeOfNode(node: PlaceholderNode, path: string): DataShape {
  // A loop whose rows are only ever printed whole (`{.}`) is a list of values — of
  // pictures when the row is a picture — not a list of objects.
  const rowItself = (node.nested ?? []).filter((n) => typeof n?.variable === 'string' && n.variable.endsWith('..'))
  const ownFields = (node.nested ?? []).length - rowItself.length
  switch (node.fieldType) {
    case 'loop':
      return {
        kind: 'list',
        row: ownFields > 0
          ? { kind: 'object', fields: shapeOfNodes(node.nested, path) }
          : { kind: rowItself.some((n) => n.fieldType === 'image') ? 'picture' : 'text' },
      }
    case 'linked':
      return { kind: 'object', fields: shapeOfNodes(node.nested, path) }
    case 'image':
      return { kind: 'picture' }
    case 'conditional':
      return { kind: node.operator && node.operator !== 'truthy' && node.operator !== 'negated' ? 'compare' : 'flag' }
    default:
      return node.nested?.length ? { kind: 'object', fields: shapeOfNodes(node.nested, path) } : { kind: 'text' }
  }
}

/**
 * The fields of one item of `data`: the record itself for a template that makes a document
 * per item; for one that makes one document from many, a row of its `{#Records}` loop (a
 * list) or the fields printed once per page or label.
 */
function itemShape(placeholdersJson: string | null | undefined, recordMode: RecordMode): Record<string, DataShape> {
  const nodes = parsePlaceholderTree(placeholdersJson)
  if (!isCombinedMode(recordMode)) return shapeOfNodes(nodes, '')
  const list = Array.isArray(nodes) ? (nodes as PlaceholderNode[]) : []
  const records = list.find((node) => node?.variable === 'Records' && node.fieldType === 'loop')
  return records
    ? shapeOfNodes(records.nested, 'Records')
    : shapeOfNodes(list.filter((node) => !COMBINED_DOCUMENT_NAMES.has(String(node?.variable ?? '').split('.')[0])), '')
}

function sampleOf(shape: DataShape): unknown {
  switch (shape.kind) {
    case 'text': return '…'
    case 'picture': return 'https://example.com/picture.png'
    case 'flag':
    case 'compare': return true
    case 'object': return sampleFields(shape.fields)
    case 'list': return [sampleOf(shape.row)]
  }
}

function sampleFields(fields: Record<string, DataShape>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(fields).map(([key, shape]) => [key, sampleOf(shape)]))
}

/**
 * Example `data` for a template, built from its stored placeholder tree, for the copyable
 * request the extension and the portal show: text as "…", a loop as a list with one row of
 * its columns, a linked field as an object, a picture as a URL, a condition as `true`.
 */
export function sampleDocumentData(placeholdersJson: string | null | undefined): Record<string, unknown> {
  return sampleFields(itemShape(placeholdersJson, 'per_record'))
}

/**
 * Example `data` for the request: an object for a template that makes a document per item,
 * a list of two items for one that makes one document from many — each item a record of
 * its `{#Records}` loop (list), or the fields the template prints once per page or label.
 */
export function sampleDocumentRequestData(
  placeholdersJson: string | null | undefined,
  recordMode: RecordMode = 'per_record',
): Record<string, unknown> | Record<string, unknown>[] {
  const item = sampleFields(itemShape(placeholdersJson, recordMode))
  return isCombinedMode(recordMode) ? [item, { ...item }] : item
}

// =============================================================================
// Schema of `data`, for workflow designers
// =============================================================================

/**
 * A JSON Schema of `data`, in the subset OpenAPI 2.0 and Power Automate's dynamic schemas
 * (`x-ms-dynamic-schema`) read: `x-ms-summary` is the label a designer shows on the input.
 */
export interface DocumentDataSchema {
  type: 'string' | 'boolean' | 'object' | 'array'
  format?: 'uri'
  'x-ms-summary'?: string
  description?: string
  properties?: Record<string, DocumentDataSchema>
  items?: DocumentDataSchema
}

/**
 * One field's schema. `placeholder` is how the field is written where it is printed —
 * `{Customer.Name}`, but `{Qty}` inside the `{#Items}` row — so the description names what
 * the template's author typed. Nothing is `required`: as in the API, a placeholder with no
 * value prints nothing.
 */
function schemaOf(shape: DataShape, key: string, placeholder: string): DocumentDataSchema {
  switch (shape.kind) {
    case 'text':
      return { type: 'string', 'x-ms-summary': key, description: `Printed where the template has {${placeholder}}.` }
    case 'picture':
      return { type: 'string', format: 'uri', 'x-ms-summary': key, description: `A public http(s) address of the picture for {${placeholder}}.` }
    case 'flag':
      return { type: 'boolean', 'x-ms-summary': key, description: `Whether the template's {#if ${placeholder}} section is shown.` }
    case 'compare':
      return { type: 'string', 'x-ms-summary': key, description: `The value the template's {#if ${placeholder} …} condition compares.` }
    case 'object':
      return { type: 'object', 'x-ms-summary': key, description: `Fills {${placeholder}.…}.`, properties: schemaFields(shape.fields, `${placeholder}.`) }
    case 'list':
      if (shape.row.kind === 'object') {
        return {
          type: 'array',
          'x-ms-summary': key,
          description: `One row of the template's {#${placeholder}} loop per entry.`,
          items: { type: 'object', properties: schemaFields(shape.row.fields, '') },
        }
      }
      return {
        type: 'array',
        'x-ms-summary': key,
        description: shape.row.kind === 'picture'
          ? `Public http(s) addresses of the pictures for {#${placeholder}}.`
          : `Printed one per row of {#${placeholder}}, or joined with commas as {${placeholder}}.`,
        items: shape.row.kind === 'picture' ? { type: 'string', format: 'uri' } : { type: 'string' },
      }
  }
}

function schemaFields(fields: Record<string, DataShape>, placeholderPrefix: string): Record<string, DocumentDataSchema> {
  return Object.fromEntries(Object.entries(fields).map(([key, shape]) => [key, schemaOf(shape, key, `${placeholderPrefix}${key}`)]))
}

/**
 * The schema of `data` for a template: an object of its fields for a template that makes a
 * document per item, a list of such objects for one that makes one document from many.
 * Its keys are placeholder names, which documentRecordFromJson() maps back to themselves
 * (sanitizeFieldName() leaves a sanitized name as it is).
 */
export function documentDataSchema(placeholdersJson: string | null | undefined, recordMode: RecordMode = 'per_record'): DocumentDataSchema {
  const item: DocumentDataSchema = { type: 'object', properties: schemaFields(itemShape(placeholdersJson, recordMode), '') }
  if (!isCombinedMode(recordMode)) return item
  return {
    type: 'array',
    'x-ms-summary': 'Records',
    description: 'One entry per record; together they make one document, in this order.',
    items: item,
  }
}

// =============================================================================
// Templates, as a key lists them
// =============================================================================

/** The request's `mode` that a template's record mode is. */
export function batchModeOfRecordMode(mode: RecordMode): DocumentApiBatchMode {
  return mode === 'per_record' ? 'separate' : mode
}

/** One template, as `GET /api/v1/templates` lists it. */
export interface ApiTemplateSummary {
  id: string
  name: string
  /** Where the template's own records come from. The document API fills every kind from JSON. */
  source: TemplateSourceType
  /** The template file's kind. */
  templateFormat: TemplateFormat
  /** What a request makes from `data` — the `mode` a request may send as a check. */
  mode: DocumentApiBatchMode
  /** The file a request makes when it does not send `format`. */
  outputFormat: OutputFormat
  /** False while the template has no file, or errors in it stop documents being made. */
  ready: boolean
}

export interface ApiTemplateListResponse {
  templates: ApiTemplateSummary[]
}

/** One template with what its `data` takes, as `GET /api/v1/templates/:templateId` returns it. */
export interface ApiTemplateDetail extends ApiTemplateSummary {
  /** The schema of `data` (documentDataSchema). */
  dataSchema: DocumentDataSchema
  /** An example `data` (sampleDocumentRequestData). */
  sampleData: Record<string, unknown> | Record<string, unknown>[]
  /** Where to POST a request for this template. */
  documentsUrl: string
}

/** A copyable curl call of the document API. The key is whatever the caller passes, masked or not. */
export function documentApiCurl(o: { url: string; apiKey: string; data: Record<string, unknown> | Record<string, unknown>[]; waitSeconds?: number }): string {
  const body = JSON.stringify({ data: o.data }, null, 2).replace(/'/g, `'\\''`)
  const url = o.waitSeconds ? `${o.url}?wait=${o.waitSeconds}` : o.url
  return [
    `curl -X POST '${url}' \\`,
    `  -H 'X-Webhook-API-Key: ${o.apiKey}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '${body}'`,
  ].join('\n')
}
